<template>
  <div class="image-file-page">
    <header class="page-heading">
      <div>
        <h1>图片文件管理器</h1>
        <p>查看硬盘与 file_asset 中的每个不可变图片文件，包括当前版本、历史版本、头像和待清理文件。</p>
      </div>
      <el-button icon="el-icon-refresh" :loading="loading" @click="refreshAll">刷新</el-button>
    </header>

    <div class="statistics">
      <el-card v-for="item in statisticCards" :key="item.key" shadow="never">
        <span>{{ item.label }}</span><strong>{{ item.value }}</strong>
      </el-card>
    </div>

    <el-tabs v-model="activeTab" type="border-card" @tab-click="handleTabChange">
      <el-tab-pane label="全部物理文件" name="files">
        <div class="filters">
          <el-input v-model.trim="filters.keyword" clearable placeholder="文件 ID、原文件名或 SHA-256" @keyup.enter.native="loadFiles" />
          <el-select v-model="filters.usage" @change="loadFiles">
            <el-option label="全部用途" value="all" />
            <el-option label="当前版本" value="current" />
            <el-option label="历史版本" value="history" />
            <el-option label="用户头像" value="avatar" />
            <el-option label="无业务引用" value="orphan" />
          </el-select>
          <el-button type="primary" @click="loadFiles">查询</el-button>
        </div>
        <el-table v-loading="loading" :data="files" stripe>
          <el-table-column label="预览" width="86">
            <template slot-scope="scope">
              <el-image class="thumb" :src="accessUrl(scope.row)" fit="contain" :preview-src-list="[accessUrl(scope.row)]" />
            </template>
          </el-table-column>
          <el-table-column label="文件" min-width="250">
            <template slot-scope="scope">
              <strong>#{{ scope.row.id }} · {{ scope.row.original_name }}</strong>
              <div class="muted">{{ scope.row.width }} × {{ scope.row.height }} · {{ formatBytes(scope.row.file_size) }}</div>
              <div class="hash" :title="scope.row.sha256">{{ scope.row.sha256 }}</div>
            </template>
          </el-table-column>
          <el-table-column label="用途" width="120">
            <template slot-scope="scope"><el-tag size="mini" :type="usageType(scope.row.classification)">{{ usageLabel(scope.row.classification) }}</el-tag></template>
          </el-table-column>
          <el-table-column label="真实引用" min-width="220">
            <template slot-scope="scope">
              <span>当前 {{ count(scope.row, 'image_current') }}</span>
              <span> · 历史 {{ count(scope.row, 'image_revision') }}</span>
              <span> · 头像 {{ count(scope.row, 'user_avatar') }}</span>
            </template>
          </el-table-column>
          <el-table-column label="文件状态" width="110" prop="status" />
          <el-table-column label="清理任务" min-width="180">
            <template slot-scope="scope">
              <span v-if="!scope.row.gc_job" class="muted">无</span>
              <el-tooltip v-else :content="scope.row.gc_job.last_error || jobLabel(scope.row.gc_job.status)" placement="top">
                <el-tag size="mini" :type="jobType(scope.row.gc_job.status)">{{ jobLabel(scope.row.gc_job.status) }}</el-tag>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="270" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" @click="openDetails(scope.row)">详情</el-button>
              <el-button v-if="canQueue(scope.row)" type="text" @click="queueCleanup(scope.row)">加入队列</el-button>
              <el-button v-if="canCancel(scope.row)" type="text" @click="cancelJob(scope.row.gc_job)">取消清理</el-button>
              <el-button
                v-if="isTopAdmin && canRetry(scope.row)"
                type="text"
                @click="retryJob(scope.row.gc_job)"
              >重试</el-button>
              <el-button
                v-if="isTopAdmin && canPurgeNow(scope.row)"
                class="danger-action"
                type="text"
                @click="purgeFileNow(scope.row)"
              >立即清理</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination v-if="filePage.total" class="pagination" background layout="total, prev, pager, next" :total="filePage.total" :page-size="filePage.page_size" :current-page="filePage.page_num" @current-change="changeFilePage" />
      </el-tab-pane>

      <el-tab-pane label="回收站" name="recycle">
        <el-alert title="回收站默认保留30天；立即删除仍会再次检查引用，不能强制删除正在使用的图片。" type="warning" :closable="false" show-icon />
        <el-table v-loading="recycleLoading" :data="recycleItems" stripe>
          <el-table-column label="预览" width="86">
            <template slot-scope="scope">
              <el-image class="thumb" :src="accessUrl(scope.row)" fit="contain" :preview-src-list="[accessUrl(scope.row)]">
                <div slot="error" class="thumb-fallback" title="图片暂时无法预览">
                  <i class="el-icon-picture-outline" />
                </div>
              </el-image>
            </template>
          </el-table-column>
          <el-table-column label="图片" min-width="260">
            <template slot-scope="scope"><strong>#{{ scope.row.id }} · {{ scope.row.name }}</strong><div class="muted">当前文件 #{{ scope.row.current_asset_id }} · {{ scope.row.version_count }} 个版本</div></template>
          </el-table-column>
          <el-table-column label="删除原因" min-width="180" prop="delete_reason" />
          <el-table-column label="彻底清理时间" width="180"><template slot-scope="scope">{{ formatTime(scope.row.purge_after) }}</template></el-table-column>
          <el-table-column label="操作" width="190" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" @click="restoreItem(scope.row)">恢复</el-button>
              <el-button size="mini" type="danger" plain @click="purgeItem(scope.row)">立即删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <el-dialog title="物理图片详情" :visible.sync="detailVisible" width="760px" append-to-body>
      <div v-loading="detailLoading" class="file-detail">
        <template v-if="currentDetail">
          <div class="detail-summary">
            <el-image class="detail-preview" :src="accessUrl(currentDetail)" fit="contain" :preview-src-list="[accessUrl(currentDetail)]">
              <div slot="error" class="thumb-fallback"><i class="el-icon-picture-outline" /></div>
            </el-image>
            <div>
              <h3>#{{ currentDetail.id }} · {{ currentDetail.original_name }}</h3>
              <p>{{ currentDetail.width }} × {{ currentDetail.height }} · {{ formatBytes(currentDetail.file_size) }} · {{ currentDetail.mime_type }}</p>
              <p>状态：{{ currentDetail.status }} · 创建：{{ formatTime(currentDetail.create_time) }}</p>
              <div class="detail-hash" :title="currentDetail.sha256">SHA-256：{{ currentDetail.sha256 }}</div>
            </div>
          </div>

          <el-alert
            :title="detailCanDelete ? '当前没有业务引用，可以安全进入清理队列。' : `当前有 ${detailReferenceCount} 个业务引用，禁止删除。`"
            :type="detailCanDelete ? 'success' : 'warning'"
            :closable="false"
            show-icon
          />

          <section class="reference-section">
            <h4>图片资源引用（{{ detailReferences('image_current').length }}）</h4>
            <el-table v-if="detailReferences('image_current').length" :data="detailReferences('image_current')" size="mini" border>
              <el-table-column prop="id" label="资源 ID" width="90" />
              <el-table-column prop="name" label="资源名称" />
              <el-table-column label="资源状态" width="110"><template slot-scope="scope">{{ scope.row.deleted_at ? '回收站' : scope.row.status }}</template></el-table-column>
            </el-table>
            <p v-else class="empty-reference">无</p>
          </section>

          <section class="reference-section">
            <h4>版本记录（{{ detailReferences('image_revision').length }}）</h4>
            <el-table v-if="detailReferences('image_revision').length" :data="detailReferences('image_revision')" size="mini" border max-height="220">
              <el-table-column prop="id" label="版本 ID" width="90" />
              <el-table-column prop="library_name" label="所属图片资源" />
              <el-table-column prop="operation" label="产生方式" width="110" />
              <el-table-column label="当前版本" width="90"><template slot-scope="scope">{{ scope.row.is_current ? '是' : '否' }}</template></el-table-column>
            </el-table>
            <p v-else class="empty-reference">无</p>
          </section>

          <section class="reference-section">
            <h4>用户头像（{{ detailReferences('user_avatar').length }}）</h4>
            <p v-if="detailReferences('user_avatar').length" class="reference-text">
              {{ detailReferences('user_avatar').map(item => `#${item.id} ${item.username}`).join('；') }}
            </p>
            <p v-else class="empty-reference">无</p>
          </section>

          <section class="reference-section">
            <h4>异常音频字段引用（{{ detailReferences('sound_audio').length }}）</h4>
            <p v-if="detailReferences('sound_audio').length" class="reference-text">
              {{ detailReferences('sound_audio').map(item => `#${item.id} ${item.name}`).join('；') }}
            </p>
            <p v-else class="empty-reference">无</p>
          </section>
        </template>
      </div>
      <span slot="footer"><el-button @click="detailVisible = false">关闭</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import apis from '@/api'

export default {
  name: 'ImageFileManager',
  data() {
    return {
      activeTab: 'files', loading: false, recycleLoading: false,
      statistics: {}, files: [], recycleItems: [],
      detailVisible: false, detailLoading: false, currentDetail: null,
      filters: { keyword: '', usage: 'all' },
      filePage: { page_num: 1, page_size: 24, total: 0 }
    }
  },
  computed: {
    isTopAdmin() { return Boolean(this.$store.getters.isTheHighestLevel) },
    detailCanDelete() { return Boolean((((this.currentDetail || {}).inspection || {}).can_delete)) },
    detailReferenceCount() { return Number((((this.currentDetail || {}).inspection || {}).business_reference_count) || 0) },
    statisticCards() {
      return [
        { key: 'total', label: '物理图片', value: Number(this.statistics.total || 0) },
        { key: 'bytes', label: '占用空间', value: this.formatBytes(this.statistics.bytes) },
        { key: 'history', label: '历史版本', value: Number(this.statistics.history || 0) },
        { key: 'recycle', label: '回收站', value: Number(this.statistics.recycle || 0) },
        { key: 'pending', label: '待处理任务', value: Number(this.statistics.pending || 0) }
      ]
    }
  },
  created() { this.refreshAll() },
  methods: {
    async refreshAll() { await Promise.all([this.loadStatistics(), this.activeTab === 'recycle' ? this.loadRecycle() : this.loadFiles(false)]) },
    async loadStatistics() {
      const response = await apis.imageFile.statistics()
      if (response.data.code === 1) this.statistics = response.data.data || {}
    },
    async loadFiles(reset = true) {
      if (reset) this.filePage.page_num = 1
      this.loading = true
      try {
        const response = await apis.imageFile.list({ ...this.filters, page_num: this.filePage.page_num, page_size: this.filePage.page_size })
        if (response.data.code !== 1) throw new Error(response.data.msg || '图片文件加载失败')
        const data = response.data.data || {}
        this.files = Array.isArray(data.list) ? data.list : []
        this.filePage.total = Number(data.total || 0)
      } catch (error) { this.$message.error(String(error.message || error)) } finally { this.loading = false }
    },
    async loadRecycle() {
      this.recycleLoading = true
      try {
        const response = await apis.imageAsset.recycleBin({ page_num: 1, page_size: 60 })
        if (response.data.code !== 1) throw new Error(response.data.msg || '回收站加载失败')
        const data = response.data.data || {}
        this.recycleItems = Array.isArray(data.list) ? data.list : (Array.isArray(data.rows) ? data.rows : [])
      } catch (error) { this.$message.error(String(error.message || error)) } finally { this.recycleLoading = false }
    },
    handleTabChange() { this.activeTab === 'recycle' ? this.loadRecycle() : this.loadFiles(false) },
    changeFilePage(page) { this.filePage.page_num = page; this.loadFiles(false) },
    async restoreItem(item) {
      const response = await apis.imageAsset.restore({ id: item.id })
      if (response.data.code !== 1) return this.$message.error(response.data.msg || '恢复失败')
      this.$message.success('图片已恢复'); await this.refreshAll()
    },
    async purgeItem(item) {
      try { await this.$confirm(`将彻底删除“${item.name}”及无其他引用的文件，此操作不可恢复。`, '立即删除', { type: 'error', confirmButtonText: '彻底删除' }) } catch (error) { return }
      const response = await apis.imageAsset.purgeNow({ id: item.id })
      if (response.data.code !== 1) return this.$message.error(response.data.msg || '删除失败')
      this.$message.success(response.data.msg); await this.refreshAll()
    },
    /** 2026-09-12：加载实时引用明细；列表中的引用数字只用于快速浏览。 */
    async openDetails(item) {
      this.detailVisible = true
      this.detailLoading = true
      this.currentDetail = null
      try {
        const response = await apis.imageFile.details({ asset_id: item.id })
        if (response.data.code !== 1) throw new Error(response.data.msg || '详情加载失败')
        this.currentDetail = response.data.data || null
      } catch (error) {
        this.$message.error(String(error.message || error))
        this.detailVisible = false
      } finally { this.detailLoading = false }
    },
    /** 2026-09-12：普通清理先进入 30 天队列，期间可以取消。 */
    async queueCleanup(item) {
      try {
        await this.$confirm(`文件 #${item.id} 将保留 30 天后再彻底删除，期间可以取消。`, '加入清理队列', {
          type: 'warning', confirmButtonText: '加入队列'
        })
      } catch (error) { return }
      try {
        const response = await apis.imageFile.queueCleanup({ asset_id: item.id })
        if (response.data.code !== 1) throw new Error(response.data.msg || '加入队列失败')
        this.$message.success(response.data.msg)
        await this.refreshAll()
      } catch (error) { this.$message.error(String(error.message || error)) }
    },
    /** 2026-09-12：立即清理不可恢复，只向最高管理员展示。 */
    async purgeFileNow(item) {
      try {
        await this.$confirm(`将立即删除文件 #${item.id}、硬盘内容和数据库记录。此操作不可恢复。`, '立即清理物理文件', {
          type: 'error', confirmButtonText: '确认永久删除'
        })
      } catch (error) { return }
      try {
        const response = await apis.imageFile.purgeNow({ asset_id: item.id })
        if (response.data.code !== 1) throw new Error(response.data.msg || '立即清理失败')
        this.$message.success(response.data.msg)
        await this.refreshAll()
      } catch (error) { this.$message.error(String(error.message || error)) }
    },
    async cancelJob(job) {
      try {
        await this.$confirm('取消后，文件会继续保留，并可在以后重新加入清理队列。', '取消清理', { type: 'warning' })
      } catch (error) { return }
      try {
        const response = await apis.imageFile.cancel({ job_id: job.id })
        if (response.data.code !== 1) throw new Error(response.data.msg || '取消失败')
        this.$message.success(response.data.msg)
        await this.refreshAll()
      } catch (error) { this.$message.error(String(error.message || error)) }
    },
    async retryJob(job) {
      try {
        const response = await apis.imageFile.retry({ job_id: job.id })
        if (response.data.code !== 1) throw new Error(response.data.msg || '重试失败')
        this.$message.success(response.data.msg)
        await this.refreshAll()
      } catch (error) { this.$message.error(String(error.message || error)) }
    },
    canQueue(item) { return item.classification === 'orphan' && item.status === 'ready' && !this.hasActiveJob(item) },
    canPurgeNow(item) {
      const status = String((((item || {}).gc_job || {}).status) || '')
      return item.classification === 'orphan' && item.status === 'ready' && !['processing', 'retry'].includes(status)
    },
    canCancel(item) { return Boolean(item.gc_job && ['pending', 'blocked'].includes(item.gc_job.status)) },
    canRetry(item) { return Boolean(item.gc_job && ['retry', 'blocked'].includes(item.gc_job.status)) },
    hasActiveJob(item) { return Boolean(item.gc_job && ['pending', 'processing', 'retry', 'blocked'].includes(item.gc_job.status)) },
    detailReferences(key) { return (((this.currentDetail || {}).inspection || {}).references || {})[key] || [] },
    // 2026-09-16：服务端已经选择文件域名，页面不再自行拼接 /apis。
    accessUrl(item) { return String((item && item.full_path) || '') },
    count(item, key) { return Number((((item || {}).inspection || {}).counts || {})[key] || 0) },
    usageLabel(value) { return ({ current: '当前版本', history: '历史版本', avatar: '用户头像', other: '异常引用', orphan: '无业务引用' })[value] || value },
    usageType(value) { return ({ current: 'success', history: '', avatar: 'warning', other: 'danger', orphan: 'danger' })[value] || 'info' },
    jobLabel(value) { return ({ pending: '等待清理', processing: '清理中', retry: '等待重试', blocked: '已阻止', done: '已完成', cancelled: '已取消' })[value] || value },
    jobType(value) { return ({ pending: 'warning', processing: 'warning', retry: 'danger', blocked: 'danger', done: 'success', cancelled: 'info' })[value] || 'info' },
    formatBytes(bytes) { const value = Number(bytes || 0); if (value < 1024) return `${value} B`; if (value < 1048576) return `${(value / 1024).toFixed(1)} KB`; return `${(value / 1048576).toFixed(2)} MB` },
    formatTime(seconds) { return seconds ? new Date(Number(seconds) * 1000).toLocaleString() : '-' }
  }
}
</script>

<style lang="scss" scoped>
.image-file-page { padding: 24px; }
.page-heading { display: flex; margin-bottom: 18px; align-items: flex-start; justify-content: space-between; }
.page-heading h1 { margin: 0; font-size: 24px; }
.page-heading p { margin: 7px 0 0; color: #7d8795; }
.statistics { display: grid; margin-bottom: 18px; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; }
.statistics ::v-deep .el-card__body { display: grid; padding: 16px; gap: 8px; }
.statistics span { color: #909399; font-size: 12px; }
.statistics strong { color: #303133; font-size: 22px; }
.filters { display: flex; max-width: 700px; margin-bottom: 16px; gap: 10px; }
.filters .el-input { flex: 1; }
.thumb { width: 56px; height: 56px; border: 1px solid #ebeef5; border-radius: 6px; background: #f5f7fa; }
.thumb-fallback { display: flex; width: 100%; height: 100%; color: #c0c4cc; font-size: 22px; align-items: center; justify-content: center; }
.muted { margin-top: 5px; color: #909399; font-size: 12px; }
.hash { overflow: hidden; max-width: 310px; margin-top: 4px; color: #b0b5bd; font-family: monospace; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.pagination { display: flex; margin-top: 18px; justify-content: flex-end; }
.danger-action { color: #f56c6c; }
.file-detail { min-height: 130px; }
.detail-summary { display: flex; margin-bottom: 18px; gap: 16px; }
.detail-summary h3 { margin: 4px 0 10px; }
.detail-summary p { margin: 6px 0; color: #606266; }
.detail-preview { flex: 0 0 120px; width: 120px; height: 120px; border: 1px solid #ebeef5; border-radius: 8px; background: #f5f7fa; }
.detail-hash { overflow-wrap: anywhere; color: #909399; font-family: monospace; font-size: 11px; }
.reference-section { margin-top: 18px; }
.reference-section h4 { margin: 0 0 8px; color: #303133; }
.empty-reference { margin: 0; color: #c0c4cc; }
.reference-text { margin: 0; color: #606266; line-height: 1.8; }
@media (max-width: 1100px) { .statistics { grid-template-columns: repeat(2, 1fr); } }
</style>
